// @flow
import React from 'react';

import FileSize from 'react-fine-uploader/filesize';
import Filename from 'react-fine-uploader/filename';
import RetryButton from 'react-fine-uploader/retry-button';
import CancelButton from 'react-fine-uploader/cancel-button';
import {LazyTooltip} from 'app/components/general/LazyTooltip';

import {gt} from 'app/utils/i18n';

type StatusesPropertiesType<T> = {
  deleting: T,
  paused: T,
  queued: T,
  retrying_upload: T,
  submitting: T,
  uploading: T,
  upload_failed: T,
  upload_successful: T,
};

const LOADING_ICON_CLASS = 'fa fa-circle-notch fa-spin big';

const STATUSES_ICONS: StatusesPropertiesType<string> = {
  deleting: LOADING_ICON_CLASS,
  paused: 'fa fa-pause',
  queued: 'far fa-clock',
  retrying_upload: LOADING_ICON_CLASS,
  submitting: LOADING_ICON_CLASS,
  uploading: LOADING_ICON_CLASS,
  upload_failed: 'fa fa-times',
  upload_successful: 'fa fa-check',
};

const STATUSES_COLORS: StatusesPropertiesType<string> = {
  deleting: 'info',
  paused: 'info',
  queued: 'secondary',
  retrying_upload: 'info',
  submitting: 'primary',
  uploading: 'primary',
  upload_failed: 'danger',
  upload_successful: 'success',
};

type Props = {
  onlyProgress?: boolean,
  files: Array<number>,
  uploader: {
    on: (eventName: string, (...any) => any) => void,
    off: (eventName: string, (...any) => any) => void,
  },
  onCancelFile: (number) => void,
  onStatusChange?: (
    status: string,
    oldStatus: string | null,
    id: number
  ) => void,
  cancelButton?: React.ReactNode,
};

type StatusTypes =
  | 'deleting'
  | 'paused'
  | 'queued'
  | 'retrying_upload'
  | 'submitting'
  | 'uploading'
  | 'upload_failed'
  | 'upload_successful';

type State = {
  filesProgress: {[number]: number},
  filesStatus: {[number]: StatusTypes},
  filesMessage: {[number]: string},
};

class FineUploaderFileList extends React.Component<Props, State> {
  statusesText: StatusesPropertiesType<string>;

  state = {
    filesProgress: {},
    filesStatus: {},
    filesMessage: {},
  };

  pendingStatuses = ['deleting', 'retrying_upload', 'submitting', 'uploading'];

  constructor(props: Props) {
    super(props);

    this.statusesText = {
      deleting: `${gt('Deleting')}...`,
      paused: gt('Paused'),
      queued: gt('Queued'),
      retrying_upload: `${gt('Retrying')}...`,
      submitting: `${gt('Submitting')}...`,
      uploading: `${gt('Uploading')}...`,
      upload_failed: gt('Failed'),
      upload_successful: gt('Upload Completed'),
    };
  }

  handleSubmitted = (id: number) => {
    this.setState({
      filesProgress: {
        ...this.state.filesProgress,
        [id]: 0,
      },
    });
  };

  handleProgress = (
    id: number,
    name: string,
    bytesUploaded: number,
    totalSize: number
  ) => {
    this.setState({
      filesProgress: {
        ...this.state.filesProgress,
        [id]: bytesUploaded / totalSize,
      },
    });
  };

  handleStatusChange = (
    id: number,
    oldStatus: string | null,
    newStatus: string
  ) => {
    const normalizedStatus = newStatus.replace(' ', '_');
    const normalizedOldStatus =
      oldStatus != null ? oldStatus.replace(' ', '_') : null;

    this.setState({
      filesStatus: {
        ...this.state.filesStatus,
        [id]: normalizedStatus,
      },
    });

    if (this.props.onStatusChange != null) {
      this.props.onStatusChange(normalizedStatus, normalizedOldStatus, id);
    }
  };

  handleError = (id: number, name: string, reason: string) => {
    this.setState({
      filesMessage: {
        [id]: reason,
      },
    });
  };

  UNSAFE_componentWillMount() {
    this.props.uploader.on('submitted', this.handleSubmitted);
    this.props.uploader.on('progress', this.handleProgress);
    this.props.uploader.on('statusChange', this.handleStatusChange);
    this.props.uploader.on('error', this.handleError);
  }

  componentWillUnmount() {
    this.props.uploader.off('submitted', this.handleSubmitted);
    this.props.uploader.off('progress', this.handleProgress);
    this.props.uploader.off('statusChange', this.handleStatusChange);
    this.props.uploader.off('error', this.handleError);
  }

  getPercentage(id: number, hideComplete: boolean) {
    const progress = this.state.filesProgress[id];
    const percent = parseInt(progress * 100);
    return percent === 100 && hideComplete ? '' : `${percent}%`;
  }

  getProgressPercentage(id: number) {
    const percentage = this.getPercentage(id, false);
    const status = this.state.filesStatus[id];

    if (this.isPending(id) || status === 'upload_successful') {
      return percentage;
    }
    return 'auto';
  }

  isPending = (id: number) => {
    return this.pendingStatuses.indexOf(this.state.filesStatus[id]) > -1;
  };

  getIconClass(id: number) {
    return STATUSES_ICONS[this.state.filesStatus[id]];
  }

  getStatusColor(id: number) {
    return STATUSES_COLORS[this.state.filesStatus[id]];
  }

  getStatusText(id: number) {
    return this.statusesText[this.state.filesStatus[id]];
  }

  getProgressText(id: number) {
    const isUploading = this.state.filesStatus[id] === 'uploading';
    return `${this.getStatusText(id)} ${
      isUploading ? this.getPercentage(id, true) : ''
    }`;
  }

  isFailed(id: number): boolean {
    return this.state.filesStatus[id] === 'upload_failed';
  }

  getFailureText(id: number) {
    return this.isFailed(id) ? this.state.filesMessage[id] : null;
  }

  render() {
    const {files, uploader, onCancelFile, onlyProgress} = this.props;

    return files.length ? (
      <ul className="list-unstyled uploads-list">
        {files.map((id) => (
          <li key={id} className="pt-1 pb-2 border-bottom">
            {onlyProgress !== true ? (
              <div className="d-flex justify-content-between align-items-center">
                <div className="pb-2 pt-1">
                  <i className={`small fa-fw ${this.getIconClass(id)}`} />{' '}
                  <Filename
                    className="small mr-1"
                    id={id}
                    uploader={uploader}
                  />
                  <FileSize
                    className="small mx-2"
                    id={id}
                    uploader={uploader}
                  />
                </div>
                <div>
                  <LazyTooltip tooltip={<small>{gt('Retry')}</small>}>
                    <span>
                      <RetryButton
                        className="btn btn-link btn-sm mx-1 px-1 py-0"
                        id={id}
                        uploader={uploader}
                      >
                        <i className="fa fa-fw fa-redo" />
                      </RetryButton>
                    </span>
                  </LazyTooltip>
                  <LazyTooltip tooltip={<small>{gt('Cancel upload')}</small>}>
                    <span onClick={() => onCancelFile(id)}>
                      {this.props.cancelButton ? (
                        this.props.cancelButton
                      ) : (
                        <CancelButton
                          className="btn btn-link btn-sm px-1 py-0"
                          id={id}
                          uploader={uploader}
                        >
                          <i className="fa fa-fw fa-times-circle" />
                        </CancelButton>
                      )}
                    </span>
                  </LazyTooltip>
                </div>
              </div>
            ) : null}
            <div className="progress">
              <div
                className={`px-1 progress-bar ${
                  this.isPending(id)
                    ? 'progress-bar-striped progress-bar-animated'
                    : ''
                } bg-${this.getStatusColor(id)}`}
                style={{width: this.getProgressPercentage(id)}}
              >
                {this.getProgressText(id)}
              </div>
            </div>
            {this.isFailed(id) ? (
              <div className="small text-danger mt-1">
                {this.getFailureText(id)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    ) : null;
  }
}

export default FineUploaderFileList;
